/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ExportContext} from '../../../types/redux';
import {
  adminAssetsViewMovedResultsSelector,
  assetMoveErrorsSelector,
  assetMoveResultSelector,
  assetMoveSelectedAssetsSelector,
  assetMoveShowDialogSelector
} from './selector';
import {showMovedAssets} from './state';

/**
  *
  * Custom hook for showing Snackbar for Moving Assets.
  *
  * ```typescript
  *
  *  import {useAssetMove} from '@ion/applications/src/pages/admin/assets;
  *
  *  const {movedAssets, showMoveAsset, selectedAssetsToMove} = useAssetMove();
  *
  * ```
  */
export const useAssetMove = () => {
  const dispatch                      = useDispatch();
  const [showSuccess, setShowSuccess] = useState(false);
  const movedAssets                   = useSelector(assetMoveResultSelector);
  const selectedAssetsToMove          = useSelector(assetMoveSelectedAssetsSelector);
  const movedAssetsErrors             = useSelector(assetMoveErrorsSelector);
  const showMoveAsset                 = useSelector(assetMoveShowDialogSelector);
  const viewMovedAssets               = useSelector(adminAssetsViewMovedResultsSelector);
  const {pending}                     = useSelector(({exporter}) => exporter.current);
  const isExporting                   = pending[ExportContext.movedDevices] === true;

  useEffect(() => {
    if (movedAssets && !showMoveAsset) {
      setShowSuccess(true);
    }
  }, [movedAssets]);

  const onCloseSnack = useCallback(() => {
    setShowSuccess(false);
  }, []);

  const onViewRecords = useCallback(() => {
    dispatch(showMovedAssets());
    setShowSuccess(false);
  }, []);

  const onCancelViewMovedAssetsDialog = useCallback(() => {
    dispatch(showMovedAssets(false));
  }, []);

  return {
    isExporting,
    movedAssets,
    showSuccess,
    onCloseSnack,
    onViewRecords,
    showMoveAsset,
    viewMovedAssets,
    movedAssetsErrors,
    selectedAssetsToMove,
    onCancelViewMovedAssetsDialog
  };
};

export default useAssetMove;
